import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { studentApi } from '@/api';
import { Badge, Button, ConfirmDialog, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';
import { formatDate, formatFileSize } from '@/utils/format';

import { DocumentViewer } from './DocumentViewer';
import {
  CERTIFICATE_ACCEPT,
  CERTIFICATE_HINT,
  CERTIFICATE_MAX_SIZE,
  DOCUMENT_TONES,
  STUDENT_CERTIFICATE_LABELS,
} from './status';

/** What the student should know about the state their certificate is in. */
const EXPLANATIONS = {
  PENDING: 'An admin will check it against the details above. We will notify you when it is done.',
  VERIFIED:
    'Checked by an admin. Changing the name, issuer, dates or credential ID sends it back for review.',
};

/**
 * The file behind one certificate on the Skills page, as a strip under it --
 * the diploma panel's twin.
 *
 * Uploading is the request for verification, so the only action offered
 * without a file is "Upload certificate". A rejection shows its reason here,
 * next to the button that fixes it.
 *
 * `certificate` is a row from `GET /students/me/certifications`.
 */
export function CertificatePanel({ certificate, onChanged }) {
  const toast = useToast();
  const input = useRef(null);
  const [removing, setRemoving] = useState(false);
  const [viewing, setViewing] = useState(false);

  const status = certificate.verification_status ?? 'UNVERIFIED';
  const hasFile = Boolean(certificate.file_name);

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadCertificationFile(certificate.id, file),
    onSuccess: () => {
      toast.success('Certificate uploaded. An admin will check it.');
      onChanged();
    },
    onError: (error) => toast.error(errorMessage(error, 'Upload failed')),
  });

  const remove = useMutation({
    mutationFn: () => studentApi.deleteCertificationFile(certificate.id),
    onSuccess: () => {
      toast.success('Certificate file removed');
      setRemoving(false);
      onChanged();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function choose(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // The server checks the bytes too; this only spares a round trip.
    if (file.size > CERTIFICATE_MAX_SIZE) {
      toast.error('File is too large. Maximum 5 MB.');
      return;
    }
    upload.mutate(file);
  }

  return (
    <div
      className={cn(
        'rounded-input border px-4 py-3',
        status === 'REJECTED'
          ? 'border-danger-200 bg-danger-50/50'
          : 'border-line bg-surface-muted/60',
      )}
    >
      <input
        ref={input}
        type="file"
        accept={CERTIFICATE_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={choose}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'grid size-9 shrink-0 place-items-center rounded-lg',
              status === 'VERIFIED'
                ? 'bg-success-50 text-success-700'
                : 'bg-brand-50 text-brand-600',
            )}
          >
            <Icon name={status === 'VERIFIED' ? 'shieldCheck' : 'fileText'} size={17} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-ink truncate text-sm font-semibold">
                {hasFile ? certificate.file_name : 'No certificate file yet'}
              </p>
              <Badge tone={DOCUMENT_TONES[status] ?? 'neutral'} dot>
                {STUDENT_CERTIFICATE_LABELS[status] ?? status}
              </Badge>
            </div>
            <p className="text-ink-muted mt-0.5 text-xs">
              {hasFile
                ? [
                    formatFileSize(certificate.size_bytes),
                    certificate.verification_submitted_at &&
                      `sent for review ${formatDate(certificate.verification_submitted_at)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : `Upload the certificate so an admin can verify it. ${CERTIFICATE_HINT}.`}
            </p>
            {status === 'REJECTED' && certificate.verification_rejection_reason && (
              <p className="text-danger-800 mt-1.5 text-sm">
                <span className="font-semibold">Rejected: </span>
                {certificate.verification_rejection_reason} Upload a new copy or correct the details
                to have it checked again.
              </p>
            )}
            {EXPLANATIONS[status] && hasFile && (
              <p className="text-ink-muted mt-1.5 text-xs">{EXPLANATIONS[status]}</p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={hasFile ? 'secondary' : 'primary'}
            isLoading={upload.isPending}
            title={hasFile ? `Replace the file for ${certificate.name}` : undefined}
            onClick={() => input.current?.click()}
          >
            <Icon name="upload" size={14} />
            {hasFile ? 'Replace' : 'Upload certificate'}
          </Button>
          {hasFile && (
            <>
              <Button
                size="sm"
                variant="secondary"
                title={`Open ${certificate.file_name}`}
                onClick={() => setViewing(true)}
              >
                <Icon name="eye" size={14} />
                View
              </Button>
              <Button
                size="sm"
                variant="ghostDanger"
                title={`Remove the file for ${certificate.name}`}
                onClick={() => setRemoving(true)}
              >
                <Icon name="trash" size={14} />
                Remove file
              </Button>
            </>
          )}
        </div>
      </div>

      {hasFile && (
        <DocumentViewer
          open={viewing}
          onClose={() => setViewing(false)}
          title={certificate.name}
          fileName={certificate.file_name}
          contentType={certificate.content_type}
          queryKey={['certification', certificate.id]}
          loadUrl={(options) => studentApi.getCertificationFileDownloadUrl(certificate.id, options)}
        />
      )}

      <ConfirmDialog
        open={removing}
        title="Remove this certificate file?"
        message={
          status === 'VERIFIED'
            ? 'The certificate stays on your profile but will no longer show as verified.'
            : 'The certificate stays on your profile. You can upload the file again at any time.'
        }
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onCancel={() => setRemoving(false)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
